import { faBell } from '@fortawesome/pro-light-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useNavigate } from '@tanstack/react-router';
import { AiProcessingWidget } from '@/components/shared/AiProcessing';
import {
  AppHeaderHelpButton,
  AppHeaderNav,
  AppHeaderRoot,
  AppHeaderThemeToggle,
  type BreadcrumbsProps,
} from '@/components/shared/AppHeader';
import { ClientUserMenu } from '@/components/shared/ClientUserMenu';
import { IconButton } from '@/components/shared/IconButton';
import { NotificationsPopover } from '@/components/shared/NotificationsSheet';
import { UploadTrayButton } from '@/components/shared/UploadTray';
import { usePermissions } from '@/hooks/usePermissions';
import { useTranslator } from '@/hooks/useTranslator';
import { hasCompany } from '@/lib/utils/session';
import { useActivityFeedStore } from '@/store/activityFeedStore';
import { useSessionStore } from '@/store/sessionStore';

/** The header customers see. */
export function ClientHeader({
  breadcrumb,
  breadcrumbTrail,
}: BreadcrumbsProps) {
  const navigate = useNavigate();
  const { getTranslation } = useTranslator({ page: 'appHeader' });
  const user = useSessionStore(s => s.user);
  const { open: notificationsOpen, setOpen: setNotificationsOpen } =
    useActivityFeedStore();
  // `GET /api/notifications` requires `network:read` regardless of which tab
  // it's serving (tasks or notifications) — hide the bell rather than open
  // it to a feed that can never load.
  const canReadNetwork = usePermissions().network.canRead;

  return (
    <AppHeaderRoot
      left={
        <AppHeaderNav
          breadcrumb={breadcrumb}
          breadcrumbTrail={breadcrumbTrail}
        />
      }
      right={
        <>
          {/* AI processing queue (opens the agentic panel on its Processing
              tab) + uploads + notifications */}
          <div className='flex items-center gap-1.5'>
            {hasCompany(user) && (
              // Wrapped rather than given the attribute directly: the widget
              // owns its own root and the tour only needs something to anchor
              // to.
              <div data-tour='ai-processing' className='flex items-center'>
                <AiProcessingWidget />
              </div>
            )}

            <div data-tour='uploads' className='flex items-center'>
              <UploadTrayButton tooltip={getTranslation('uploads')} />
            </div>

            {canReadNetwork && (
              <NotificationsPopover
                open={notificationsOpen}
                onOpenChange={setNotificationsOpen}
                openOnHover
                onTriggerClick={() => navigate({ to: '/activity-feed' })}
                trigger={
                  <IconButton
                    variant='ghost'
                    size='icon'
                    className='relative h-7.5 w-7.5 text-muted-foreground'
                    tooltip={getTranslation('notifications')}
                    data-tour='notifications'
                  >
                    <FontAwesomeIcon icon={faBell} className='text-sm' />
                  </IconButton>
                }
              />
            )}
          </div>

          <AppHeaderHelpButton />
          <AppHeaderThemeToggle />

          <div className='w-px h-4.5 bg-border mx-1' />

          <div data-tour='profile' className='flex items-center'>
            <ClientUserMenu />
          </div>
        </>
      }
    />
  );
}
